import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { FaDownload, FaApple, FaAndroid } from "react-icons/fa";
import { FaFacebookF, FaTwitter, FaInstagram, FaYoutube } from "react-icons/fa";
import posthog from "posthog-js";
import { trackMetaInstallLead } from "../../utils/metaPixel";
import "./css/customs/landingPage.css";

const ANDROID_URL =
  "https://play.google.com/store/apps/details?id=com.neuralworks.quran75";
const IOS_URL =
  "https://apps.apple.com/us/app/qurania-learn-quran-with-ai/id6749280274?platform=iphone";

/**
 * Home-style CTA ("Ready to Start Your Journey?") + site footer.
 * @param {string} [downloadTrackingPage] — PostHog `page` field for download clicks (e.g. landing_page).
 */
export default function SiteCtaAndFooter({ downloadTrackingPage = "site" }) {
  const trackingRef = useRef({});

  const trackDownloadClick = (platform) => {
    const key = `${platform}-${Math.floor(Date.now() / 1000)}`;
    if (trackingRef.current[key]) return;
    trackingRef.current[key] = true;
    setTimeout(() => {
      delete trackingRef.current[key];
    }, 2000);

    posthog.capture("download_button_clicked", {
      platform,
      timestamp: new Date().toISOString(),
      page: downloadTrackingPage,
      device_type: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent)
        ? "mobile"
        : "desktop",
    });

    trackMetaInstallLead(platform);

    if (posthog && typeof posthog.flush === "function") {
      posthog.flush();
    }
  };

  const handleDownloadInteraction = (platform) => {
    trackDownloadClick(platform);
  };

  return (
    <>
      <div id="cta-section" className="cta-section animate-on-scroll">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Start Your Journey?</h2>
            <p className="cta-description" style={{ color: "black" }}>
              Download the Qurania app today and begin your Quranic Arabic learning adventure!
            </p>

            <div className="cta-buttons">
              <a
                href={ANDROID_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="download-btn download-btn-android"
                onMouseDown={() => handleDownloadInteraction("Android")}
                onTouchStart={() => handleDownloadInteraction("Android")}
                onClick={() => handleDownloadInteraction("Android")}
              >
                <FaAndroid className="btn-icon" />
                <div className="btn-content">
                  <span className="btn-label">Download for</span>
                  <span className="btn-platform">Android</span>
                </div>
                <FaDownload className="btn-download-icon" />
              </a>
              <a
                href={IOS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="download-btn download-btn-ios"
                onMouseDown={() => handleDownloadInteraction("iPhone")}
                onTouchStart={() => handleDownloadInteraction("iPhone")}
                onClick={() => handleDownloadInteraction("iPhone")}
              >
                <FaApple className="btn-icon" />
                <div className="btn-content">
                  <span className="btn-label">Download for</span>
                  <span className="btn-platform">iPhone</span>
                </div>
                <FaDownload className="btn-download-icon" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <footer className="site-footer" style={{ paddingTop: "50px" }}>
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <h2>Qurania App</h2>
              <p>
                Learn Quranic Arabic FAST &amp; EASY with AI! Our innovative approach combines
                storytelling with gamified learning to make memorization enjoyable and effective.
              </p>
            </div>

            <div className="footer-links">
              <h3>Quick Links</h3>
              <ul>
                <li>
                  <Link to="/#hero-section">Home</Link>
                </li>
                <li>
                  <Link to="/learn-quranic-arabic">Learn Quranic Arabic</Link>
                </li>
                <li>
                  <Link to="/blog">Blog</Link>
                </li>
                <li>
                  <Link to="/free">Free Resources</Link>
                </li>
                <li>
                  <a href="/#privacy">Privacy Policy</a>
                </li>
                <li>
                  <a href="/#terms">Terms of Service</a>
                </li>
              </ul>
            </div>

            <div className="footer-social">
              <h3>Connect With Us</h3>
              <div className="social-icons">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <FaFacebookF />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <FaTwitter />
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <FaInstagram />
                </a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                  <FaYoutube />
                </a>
              </div>
              <p className="mt-3">
                Email: <a href="mailto:support@quranstories.pro">support@quranstories.pro</a>
              </p>
            </div>
          </div>

          <div className="footer-bottom">
            <p>
              Copyright © 2025{" "}
              <Link to="/#hero-section">Qurania App</Link>. All rights reserved.
            </p>
            <p>
              Designed with{" "}
              <span role="img" aria-label="love">
                ❤️
              </span>{" "}
              for Quranic learning
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
